// @ts-strict-ignore
import React, { useRef } from 'react';
import type { Action, NodeEditorTab } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import type { UseFormProps } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import type { FormControlMap } from './types';
import { getFormAdapter, getActionTabKey } from './helpers';
import { useAppSelector } from '~/store/reduxHooks';
import { selectWorkflowProps, selectPublishedAttributes } from '~/dashboard/views/workflows/store/redux/selectors';

import type { FormState as InputAttributeConfigurationFormState } from '../AttributeConfiguration/InputAttributeConfigurationTab';
import type { FormState as MatrixOptionsFormState } from '../MatrixOptionsTab';
import type { FormState as OutputAttributeExpressionFormState } from '../OutputAttributeExpressionTab';
import type { FormState as OutputAttributeOptionsFormState } from '../AttributeConfiguration/OutputAttributeOptionsTab';
import type { FormState as ServiceLocalSettingsFormState } from '../ServiceTabs/ServiceLocalSettingsTab';
import type { FormState as TagGlobalSettingsFormState } from '../TagGlobalSettingsTab';
import type { FormState as TagLocalSettingsFormState } from '../TagLocalSettingsTab';

/**
 * Handles all of the form creation logic for all NodeEditorDrawer forms.
 * This hook also deals with resetting a form to its default values whenever a node is focused.
 *
 * @param focusedAction action for which to load a form controller
 * @param tab tab for which to load a form controller
 * @param extraFormProps - Anything you can pass to react-hook-form's `useForm`
 * @returns a form controller for the specified action+tab combination with full type safety.
 */
export default function useNodeEditorForm(
  focusedAction: Action,
  tab: NodeEditorTab,
  extraFormProps?: UseFormProps<any>,
) {
  const workflowProps = useAppSelector(selectWorkflowProps);
  const publishedAttributes = useAppSelector(selectPublishedAttributes);

  // define custom react hook form props
  const formProps: UseFormProps<any> = {
    mode: 'onTouched',
    ...extraFormProps,
  };
  // props are selected here to keep form control in sync with the store
  /**
   * This map indexes an action type + tab combination with a corresponding form controller.
   * A node can have more than one form, as it might have more than one drawer tab with a
   * form in it.
   */
  const formControlMap: FormControlMap = {
    [getActionTabKey(ActionType.INPUT, 'configuration')]: useForm<InputAttributeConfigurationFormState>(formProps),
    [getActionTabKey(ActionType.MATRIX_MODEL, 'options')]: useForm<MatrixOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'options')]: useForm<OutputAttributeOptionsFormState>(formProps),
    [getActionTabKey(ActionType.OUTPUT, 'rule')]: useForm<OutputAttributeExpressionFormState>(formProps),
    [getActionTabKey(ActionType.SERVICE, 'configuration')]: useForm<ServiceLocalSettingsFormState>(formProps),
    [getActionTabKey(ActionType.TAG, 'configuration')]: useForm<TagGlobalSettingsFormState>(formProps),
    [getActionTabKey(ActionType.TAG, 'options')]: useForm<TagLocalSettingsFormState>(formProps),
    // add other form controllers here
  };

  const formKey = getActionTabKey(focusedAction?.type, tab);
  const formControl = formControlMap[formKey];

  /**
   * Calls the `reset()` function on the corresponding form controller when reset conditions are met:
   * - new node is focused, new tab is opened, action values have changed, PAs have changed (race condition)
   * This populates the forms with the specific values returned from each instance of
   * the default values function for the given form adapter.
   */

  const prevFormResetKey = useRef<string>('');

  const getFormResetKey = (action: Action, drawerTab: NodeEditorTab, paCount: number): string => {
    if (!action) return '';
    return JSON.stringify({
      // focused node changed
      publicId: action.publicId,
      // tab changed (same node)
      drawerTab,
      // values changed (same node)
      tagOutcomeKeys: (action.tag?.outcomes ?? []).map(o => o.outcome).sort().join(','),
      hasPA: Boolean(action.input?.publishedAttributeToken
        || action.output?.publishedAttributeToken),
      paCount,
    });
  };

  React.useEffect(() => {
    if (!focusedAction || !formControl) {
      return;
    }

    const currentKey = getFormResetKey(focusedAction, tab, publishedAttributes.length);
    const hasDefinedFormValues = (
      Object.values(formControl?.getValues() ?? {}).some(value => value !== undefined)
    );

    const shouldReset = prevFormResetKey.current !== currentKey || !hasDefinedFormValues;

    if (shouldReset) {
      const formAdapter = getFormAdapter(focusedAction, tab);
      formControl.reset(formAdapter.getDefaultFormValues(focusedAction, workflowProps));
      prevFormResetKey.current = currentKey;
    }
  }, [focusedAction, formControl, tab, workflowProps, publishedAttributes]);

  return formControl;
}

